"use client";
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

interface HeroSectionProps {
  isVisible: boolean;
  onVisibilityChange: (visible: boolean) => void;
}

export default function HeroSection({ isVisible, onVisibilityChange }: HeroSectionProps) {
  const sectionRef = useRef(null);
  const t = useTranslations();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisibilityChange(true);
        }
      },
      {
        threshold: 0.02,
        rootMargin: '0px 0px 50px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [onVisibilityChange]);

  return (
    <>
      {/* Global Styles for Hero */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes drawLine {
          0% { stroke-dasharray: 1000; stroke-dashoffset: 1000; }
          40% { stroke-dasharray: 1000; stroke-dashoffset: 0; }
          60% { stroke-dasharray: 1000; stroke-dashoffset: 0; }
          100% { stroke-dasharray: 1000; stroke-dashoffset: -1000; }
        }
        
        .hero-bg-1 {
          filter: blur(150px);
        }
        
        .hero-bg-2 {
          filter: blur(150px);
        }
        
        .hero-bg-3 {
          filter: blur(120px);
        }
        
        @media (max-width: 768px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
            text-align: center !important;
          }
          
          .hero-title {
            font-size: 32px !important;
            line-height: 1.2 !important;
          }
          
          .hero-subtitle {
            font-size: 18px !important;
            padding-left: 0 !important;
          }
          
          .hero-map {
            transform: scale(1) translateX(0%) !important;
            order: -1;
          }
          
          .hero-padding {
            padding: 130px 12px 80px 12px !important;
            overflow: visible !important;
          }
          
          .hero-bg-1 {
            filter: none !important;
            background: radial-gradient(circle at center, rgba(221, 3, 3, 0.6) 0%, rgba(221, 3, 3, 0.4) 15%, rgba(221, 3, 3, 0.25) 30%, rgba(221, 3, 3, 0.15) 45%, rgba(221, 3, 3, 0.08) 60%, rgba(221, 3, 3, 0.04) 75%, transparent 100%) !important;
            width: 100% !important;
            height: 300px !important;
            top: -50px !important;
            left: 0 !important;
            transform: none !important;
          }
          
          .hero-bg-2 {
            filter: none !important;
            background: radial-gradient(circle at center, rgba(217, 13, 52, 0.3) 0%, rgba(217, 13, 52, 0.2) 20%, rgba(217, 13, 52, 0.12) 40%, rgba(217, 13, 52, 0.06) 60%, rgba(217, 13, 52, 0.03) 80%, transparent 100%) !important;
          }
          
          .hero-bg-3 {
            filter: none !important;
            background: radial-gradient(circle at center, rgba(181, 31, 36, 0.2) 0%, rgba(181, 31, 36, 0.15) 25%, rgba(181, 31, 36, 0.1) 50%, rgba(181, 31, 36, 0.05) 75%, transparent 100%) !important;
          }
        }

        @media (max-width: 480px) {
          .hero-title {
            font-size: 28px !important;
          }
          
          .hero-padding {
            padding: 120px 16px 60px 16px !important;
          }
        }
      `}} />

      <section 
        ref={sectionRef} 
        style={{
          background: "#000",
          padding: "100px 24px 80px 24px",
          position: "relative",
          overflow: "hidden",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
        }}
        className="hero-padding"
      >
        {/* Background gradients */}
        <div 
          className="hero-bg-1"
          style={{
            position: "absolute",
            left: "50%",
            top: "0px",
            transform: "translateX(-50%)",
            width: "900px",
            height: "200px",
            background: "radial-gradient(50% 50% at 50% 50%,rgb(221, 3, 3) 60%, rgba(217, 13, 52, 0) 100%)",
            zIndex: 1,
            opacity: 0.6,
          }} 
        />
        
        <div 
          className="hero-bg-2"
          style={{
            position: "absolute",
            left: "-200px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "400px",
            height: "400px",
            background: "radial-gradient(50% 50% at 50% 50%, #D90D34 0%, rgba(217, 13, 52, 0) 100%)",
            zIndex: 1,
            opacity: 0.3,
          }} 
        />
        
        <div 
          className="hero-bg-3"
          style={{
            position: "absolute",
            right: "-200px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "300px",
            height: "300px",
            background: "radial-gradient(50% 50% at 50% 50%, #B51F24 0%, rgba(181, 31, 36, 0) 100%)",
            zIndex: 1,
            opacity: 0.2,
          }} 
        />

        <div style={{
          maxWidth: "1600px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "60px",
          alignItems: "center",
          position: "relative",
          zIndex: 10,
          width: "100%",
        }}
        className="hero-grid">

          {/* Left side - Text content */}
          <div style={{ 
            position: "relative", 
            zIndex: 100, 
            paddingRight: "20px",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateX(0px)' : 'translateX(-40px)',
            transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transitionDelay: "0.2s",
          }}>
            <h1 style={{
              fontSize: "42px",
              fontWeight: 600,
              color: "#fff",
              lineHeight: 1.1,
              marginBottom: "28px",
              letterSpacing: "-0.02em",
            }} 
            className="hero-title">
              {t('oldFurnitureRemoval.hero.title')}
            </h1>
            
            <h2 style={{
              fontSize: "22px",
              fontWeight: 400,
              color: "#fff",
              marginBottom: "36px",
              lineHeight: 1.3,
              position: "relative",
              paddingLeft: "24px",
            }} 
            className="hero-subtitle">
              <span style={{
                position: "absolute",
                left: "0",
                top: "0",
                bottom: "0",
                width: "3px",
                background: "#F20C2C",
                borderRadius: "2px",
              }}></span>
              {t('oldFurnitureRemoval.hero.subtitle')}
            </h2>
            
            <div style={{
              fontSize: "16px",
              color: "rgba(255,255,255,0.85)",
              lineHeight: 1.6,
                          opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transitionDelay: "0.3s",
            }}>
              <p style={{ marginBottom: "24px" }}>
                {t('oldFurnitureRemoval.hero.paragraph1')}
              </p>
              <p>
                {t('oldFurnitureRemoval.hero.paragraph2')}
              </p>
            </div>
          </div>

          {/* Right side - Map image */}
          <div style={{
            position: "relative",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            zIndex: 100,
            width: "100%",
            overflow: "visible",
          }}>
            <div style={{ 
              position: "relative", 
              transform: "scale(1.4) translateX(10%)",
              width: "100%",
            }}
            className="hero-map">
              <Image
                src="/images/components/map.png"
                alt="International Moving Map"
                width={1600}
                height={1000}
                style={{ 
                  width: "100%",
                  maxWidth: "2200px",
                  height: "auto",
                }}
                priority
              />
              
              {/* Animated SVG overlay */}
              <svg 
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                  zIndex: 10,
                }}
                viewBox="150 200 200 600"
              >
                <defs>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                    <feMerge> 
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                  <linearGradient id="pinGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor: "#FF1744", stopOpacity: 1}} />
                    <stop offset="50%" style={{stopColor: "#D90D34", stopOpacity: 1}} />
                    <stop offset="100%" style={{stopColor: "#880105", stopOpacity: 1}} />
                  </linearGradient>
                </defs>
                
                {/* Animated paths */}
                <path 
                  d="M 650 300 Q 400 250 150 400"
                  stroke="#ffffff"
                  strokeWidth="1"
                  fill="none"
                  strokeDasharray="1000"
                  strokeDashoffset="1000"
                  style={{
                    animation: "drawLine 4s ease-in-out infinite",
                    animationDelay: "0.3s",
                    filter: "url(#glow) drop-shadow(0 0 4px rgba(255,255,255,0.6))",
                  }}
                />
                
                <path 
                  d="M 650 300 Q 400 200 100 300"
                  stroke="#ffffff"
                  strokeWidth="1"
                  fill="none"
                  strokeDasharray="1000"
                  strokeDashoffset="1000"
                  style={{
                    animation: "drawLine 4s ease-in-out infinite",
                    animationDelay: "0.6s",
                    filter: "url(#glow) drop-shadow(0 0 4px rgba(255,255,255,0.6))",
                  }}
                />
              </svg>
            </div>
          </div>
        </div>
      </section>
    </>
  );
} 